import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  buildTimedAssignmentsByCrewMember,
  computeShiftProgress,
  selectCurrentAndNext,
  formatTimeLeft,
} from "@produce/shared";
import { supabase } from "@/integrations/supabase/client";
import { briefingShiftWindowResolver } from "@/lib/crewShiftWindows";
import { useMinuteTick } from "@/hooks/useMinuteTick";

/** The assignment columns every crew-map surface reads. */
export interface CrewShiftAssignment {
  id: string;
  crew_member_id: string | null;
  event_date: string;
  filming_start: string;
  filming_end: string;
  is_active: boolean | null;
  stage_name: string;
  artist_name: string | null;
  subject: string | null;
  notes: string | null;
  lineup_id: string | null;
}

/** What a crew member is on right now, and what follows it. */
export interface CrewShiftState {
  /** 0–1 while an assignment is running, null when the crew member is free. */
  progress: number | null;
  /** "42 min left" while one is running, null otherwise. */
  timeLeftLabel: string | null;
  current: CrewShiftAssignment | null;
  next: CrewShiftAssignment | null;
}

/**
 * The shift state of every crew member on one production day, keyed by
 * crew_member id.
 *
 * This is what fills the progress ring around a crew marker and the
 * "NOW / NEXT UP" lines beside it. All four crew-map surfaces read it
 * from here so they cannot disagree about who is on what.
 *
 * Two memos on purpose: turning wall-clock times into instants goes
 * through Intl and only depends on the data, while the progress maths is
 * arithmetic that has to re-run on every clock tick. Folding them
 * together would redo the expensive half every minute for an identical
 * result.
 *
 * The clock is useMinuteTick rather than a setInterval: browsers throttle
 * intervals in background tabs, which would leave a producer returning to
 * the tab looking at frozen rings.
 */
export function useCrewShiftProgress(
  briefingId: string | null | undefined,
  eventDate: string | null | undefined,
  timezone?: string,
): { shiftStateByCrewMember: Map<string, CrewShiftState> } {
  const { data: assignments } = useQuery({
    queryKey: ["crew-assignments-day", briefingId, eventDate],
    queryFn: async (): Promise<CrewShiftAssignment[]> => {
      const { data, error } = await supabase
        .from("crew_assignments")
        .select(
          "id, crew_member_id, event_date, filming_start, filming_end, is_active, stage_name, artist_name, subject, notes, lineup_id",
        )
        .eq("briefing_id", briefingId!)
        .eq("event_date", eventDate!)
        .eq("is_active", true);

      if (error) throw error;
      return data || [];
    },
    enabled: !!briefingId && !!eventDate,
    // Assignments change far less often than positions do; the ring moves
    // because the clock moves, not because this refetches.
    staleTime: 60_000,
  });

  const timedAssignmentsByCrewMember = useMemo(
    () => buildTimedAssignmentsByCrewMember(assignments, briefingShiftWindowResolver(timezone)),
    [assignments, timezone],
  );

  const nowMs = useMinuteTick().getTime();

  const shiftStateByCrewMember = useMemo(() => {
    const stateById = new Map<string, CrewShiftState>();

    timedAssignmentsByCrewMember.forEach((entries, crewMemberId) => {
      const { current, next } = selectCurrentAndNext(entries, nowMs);
      if (!current && !next) return;

      const { minutesLeft, progress } = computeShiftProgress(current?.window ?? null, nowMs);
      stateById.set(crewMemberId, {
        progress: current ? progress : null,
        timeLeftLabel: current ? formatTimeLeft(minutesLeft) : null,
        current: current?.assignment ?? null,
        next: next?.assignment ?? null,
      });
    });

    return stateById;
  }, [timedAssignmentsByCrewMember, nowMs]);

  return { shiftStateByCrewMember };
}
